import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Spinner } from "@volca/ui/components/ui/spinner";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { RequestApprovalDialogBody } from "./request-approval-dialog-body";
import type { ISheetApprovalRequestState } from "./types";
import { useApprovalRecipientSelection } from "./use-approval-recipient-selection";
import { getSendButtonLabel } from "./utils";

interface RequestApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (userIds: string[]) => void;
  isSending: boolean;
  state: ISheetApprovalRequestState;
  approvalStatus: IPayrollSheetApprovalStatus | null | undefined;
}

export const RequestApprovalDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isSending,
  state,
  approvalStatus,
}: RequestApprovalDialogProps) => {
  const { selectedIds, toggle, toggleAll } = useApprovalRecipientSelection(
    open,
    state,
    approvalStatus,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto] sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Request approval?</DialogTitle>
          <DialogDescription className="sr-only">
            Choose which approvers to email for this pay period
          </DialogDescription>
        </DialogHeader>
        <RequestApprovalDialogBody
          state={state}
          approvalStatus={approvalStatus}
          selectedIds={selectedIds}
          onToggle={toggle}
          onToggleAll={toggleAll}
        />
        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => onConfirm(selectedIds)}
            disabled={isSending || selectedIds.length === 0}
            aria-busy={isSending || undefined}
          >
            {isSending && <Spinner data-icon="inline-start" aria-hidden />}
            {getSendButtonLabel(selectedIds.length)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
